import Fuse from 'fuse.js';
import { MB_Tag } from './types';

const searchUtils = {
  /**
   * Perfroms a fuzzy search on name field of the MB_Tag.
   * The final score and the sort order is calculated based on score that we get from search + count of the MB_Tag.
   * weight of the search is 0.8 and the weight of the count is 0.2
   * @param tagsArray - Array of tags to perform the search on
   * @param titleToSearch - string to search for
   * @param excludeTags - Array of tags to exclude from search. Useful to exclude already picket tags
   * @returns - Searched and sorted MB_Tag array
   */
  tagSearch: (tagsArray: MB_Tag[], titleToSearch: string, excludeTags?: MB_Tag[]) => {
    let filteredTagsArray = tagsArray;
    if (excludeTags) {
      filteredTagsArray = filteredTagsArray.filter(filteredTag => {
        const tagIndex = excludeTags.findIndex(tag => {
          if (tag._id && filteredTag._id) {
            return tag._id === filteredTag._id;
          }
          return tag.title === filteredTag.title;
        });
        return tagIndex === -1;
      });
    }

    const options: Fuse.IFuseOptions<MB_Tag> = {
      includeScore: true,
      keys: [
        {
          name: 'title',
          weight: 1,
        },
      ],
      fieldNormWeight: 1,
    };

    // Normalizes the given number between 0 and 0.2
    const countNormalizer = (numToNormalize: number, max: number, min: number, lowerLimit: number, upperLimit: number) => {
      if (max <= min) {
        return upperLimit;
      }
      const normalizedValue = ((numToNormalize - min) / (max - min)) * (upperLimit - lowerLimit) + lowerLimit;
      return normalizedValue;
    };

    const fuse = new Fuse(filteredTagsArray, options);

    // Invert the score so that higher values are the better matches. Will help later when we are adding score for counts
    const searchResult = fuse.search(titleToSearch).map(fuseResult => {
      return { ...fuseResult, score: 1 - (fuseResult.score ?? 1) };
    });

    const biggestCount = Math.max(...searchResult.map(itm => itm.item.count ?? -Infinity));
    const smallestCount = Math.min(...searchResult.map(itm => itm.item.count ?? Infinity));
    const biggestScore = Math.max(...searchResult.map(itm => itm.score));
    const smallestScore = Math.min(...searchResult.map(itm => itm.score));

    const fixedScoreSearchResult = searchResult
      .map(fuseResult => {
        const normalizedCount = countNormalizer(fuseResult.item.count ?? 0, biggestCount, smallestCount, 0, 0.2);
        const normalizedScore = countNormalizer(fuseResult.score ?? 1, biggestScore, smallestScore, 0, 0.8);
        return { ...fuseResult, score: normalizedCount + normalizedScore };
      })
      .sort((a, b) => b.score - a.score);

    return fixedScoreSearchResult;
  },
};

export default searchUtils;
